import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectIsGloballyUnlocked,
  selectRemainingTime,
  selectIdleRemainingTime,
  selectIsWarningNeeded,
  lockGlobally,
} from '../../store/slices/lockSessionSlice';

// 格式化剩余时间
const formatRemainingTime = (ms: number): string => {
  if (ms <= 0) return '即将锁定';

  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);

  if (minutes > 0) {
    return `${minutes}分${seconds}秒`;
  }
  return `${seconds}秒`;
};

// 圆形进度条组件
interface CircularProgressProps {
  size: number;
  strokeWidth: number;
  progress: number; // 0-100
  color: string;
  warningColor: string;
  isWarning: boolean;
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  size,
  strokeWidth,
  progress,
  color,
  warningColor,
  isWarning,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {/* 背景圆环 */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={isWarning ? '#fed7aa' : '#bbf7d0'}
        strokeWidth={strokeWidth}
      />
      {/* 进度圆环 */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={isWarning ? warningColor : color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-1000 ease-linear"
      />
    </svg>
  );
};

const LockStatusIndicator: React.FC = () => {
  const dispatch = useDispatch();
  const isGloballyUnlocked = useSelector(selectIsGloballyUnlocked);
  const remainingTime = useSelector(selectRemainingTime);
  const idleRemainingTime = useSelector(selectIdleRemainingTime);
  const isWarningNeeded = useSelector(selectIsWarningNeeded);
  const [isHovered, setIsHovered] = useState(false);
  const hideTimerRef = useRef<number | null>(null);

  // 提示出现 5 秒后自动关闭（移动端触摸不会触发 mouseleave，提示会一直残留）
  const showTooltip = useCallback(() => {
    setIsHovered(true);
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = window.setTimeout(() => {
      setIsHovered(false);
      hideTimerRef.current = null;
    }, 10000);
  }, []);

  const hideTooltip = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setIsHovered(false);
  }, []);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, []);

  // 计算显示的时间（取空闲超时和绝对超时的较小值）
  const displayTime = useMemo(() => {
    return Math.min(remainingTime, idleRemainingTime);
  }, [remainingTime, idleRemainingTime]);

  // 计算进度百分比（基于绝对超时）
  const progress = useMemo(() => {
    const totalTime = 30 * 60 * 1000; // 30分钟
    const elapsed = totalTime - remainingTime;
    return Math.max(0, Math.min(100, (elapsed / totalTime) * 100));
  }, [remainingTime]);

  // 如果没有全局解锁，不显示任何内容
  if (!isGloballyUnlocked) {
    return null;
  }

  const handleLockNow = () => {
    dispatch(lockGlobally());
  };

  const size = 56; // 圆形直径
  const strokeWidth = 4; // 进度条宽度

  return (
    <div
      className="fixed bottom-6 right-6 z-50"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {/* 悬停提示 */}
      {isHovered && (
        <div className="absolute bottom-full right-0 mb-3 px-4 py-3 bg-gray-800 dark:bg-gray-700 text-white text-sm rounded-lg shadow-lg whitespace-nowrap">
          <div className="font-medium mb-1">笔记已解锁</div>
          <div className="text-gray-300 text-xs">
            剩余时间: {formatRemainingTime(displayTime)}
          </div>
          <div className="text-gray-400 text-xs mt-1">
            点击立即锁定
          </div>
          {/* 小三角箭头 */}
          <div className="absolute top-full right-5 transform -translate-x-1/2">
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800 dark:border-t-gray-700"></div>
          </div>
        </div>
      )}

      {/* 圆形按钮 */}
      <button
        onClick={handleLockNow}
        className="relative w-14 h-14 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-transform hover:scale-105 active:scale-95"
        title="点击立即锁定所有笔记"
      >
        {/* 进度条背景 */}
        <div className="absolute inset-0">
          <CircularProgress
            size={size}
            strokeWidth={strokeWidth}
            progress={progress}
            color="#22c55e"
            warningColor="#f97316"
            isWarning={isWarningNeeded}
          />
        </div>

        {/* 内部圆形背景 */}
        <div
          className={`absolute inset-1 rounded-full flex items-center justify-center transition-colors ${
            isWarningNeeded
              ? 'bg-orange-100 dark:bg-orange-900/30'
              : 'bg-green-100 dark:bg-green-900/30'
          }`}
        >
          {/* 锁图标 */}
          <svg
            className={`w-6 h-6 transition-colors ${
              isWarningNeeded
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-green-600 dark:text-green-400'
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
            />
          </svg>
        </div>
      </button>
    </div>
  );
};

export default LockStatusIndicator;
